import type { StatusTone } from "./status";
import type { ChargePoint, ConfigKeyReport } from "./types";

/**
 * Spec 4.4's configuration report, translated for an operator.
 *
 * Pure and separate from the component that renders it, because the interesting
 * part is the judgement — which states are faults, which resolve themselves, and
 * what a person is supposed to do next — and none of that needs a DOM to check.
 */

const TONES: Readonly<Record<ConfigKeyReport["state"], StatusTone>> = {
  applied: "available",
  /** Accepted, not in force yet. The next restart fixes it. */
  "reboot-required": "transitional",
  /** Unproven rather than failed, and held apart from the failures on purpose. */
  unverified: "transitional",
  ignored: "faulted",
  rejected: "faulted",
  readonly: "faulted",
  unsupported: "faulted",
};

const EXPLANATION: Readonly<Record<ConfigKeyReport["state"], string>> = {
  applied: "Reported back as set.",
  ignored: "Accepted, then not applied. Firmware quirk — retry from the commands panel.",
  readonly: "The station reports this key as fixed. It cannot be changed remotely.",
  rejected: "The station refused the change.",
  unsupported: "This firmware does not have this key.",
  "reboot-required": "Accepted, in force after the station restarts.",
  unverified: "The read-back did not complete, so this is unconfirmed.",
};

/**
 * `idle`, not `faulted`, for a state this build has never heard of. The server can
 * add one before the panel knows about it, and colouring an unknown string red
 * trains the operator to ignore red.
 */
export function configKeyTone(state: ConfigKeyReport["state"]): StatusTone {
  return TONES[state] ?? "idle";
}

export function explainConfigState(state: ConfigKeyReport["state"]): string {
  return EXPLANATION[state] ?? "The server recorded a state this panel does not recognise.";
}

/**
 * Names the offending keys rather than gesturing at them.
 *
 * Spec 4.4's two metering keys are the difference between ~72k and ~1.4M meter
 * rows a day, so a station ignoring `MeterValueSampleInterval` is a storage
 * problem with a date on it. "Configuration differs" is not something anyone can
 * act on; a key name is.
 */
export function driftLede(row: ChargePoint | undefined): string | undefined {
  if (row?.configDriftDetected !== true) return undefined;

  const offending = (row.configReport ?? [])
    .filter((entry) => entry.state !== "applied")
    .map((entry) => entry.key);

  // The flag is true either way, so a row with no detail still gets a warning —
  // just a vaguer one. Silence would be the one wrong answer here.
  if (offending.length === 0) {
    return "This station's reported configuration differs from the policy the server pushed at boot.";
  }

  return `This station did not apply ${offending.join(", ")}. See Configuration below.`;
}
